import { Component } from '@theme/component';

/**
 * @typedef {Object} SfMegaMenuRefs
 * @property {HTMLElement[]} panel - Mega menu panels, each with a `data-menu-title` attribute
 */

/**
 * Mega menu component for Sterling Forever.
 *
 * Manages hover-activated dropdown panels with open/close delays,
 * height animation, keyboard dismiss, and click-outside closing.
 *
 * @extends {Component<SfMegaMenuRefs>}
 */
class SfMegaMenu extends Component {
  /** @type {number | null} */
  #openDelay = null;

  /** @type {number | null} */
  #closeDelay = null;

  /** @type {HTMLElement | null} */
  #activePanel = null;

  /** @type {HTMLElement | null} */
  #activeTrigger = null;


  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('click', this.#handleClickOutside);
    document.addEventListener('focusin', this.#handleFocusIn);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener('click', this.#handleClickOutside);
    document.removeEventListener('focusin', this.#handleFocusIn);
    this.#clearTimers();
  }

  /**
   * Activates a mega menu panel after a short delay.
   * Called via `on:pointerenter="/activate"` on menu triggers.
   *
   * @param {PointerEvent} event
   */
  activate(event) {
    const found = this.#resolveTrigger(event.target);
    if (!found) return;

    this.#clearClose();

    if (found.panel === this.#activePanel) return;

    this.#openDelay = window.setTimeout(() => {
      this.#openDelay = null;
      this.#openPanel(found.panel, found.trigger);
    }, 80);
  }


  /**
   * Opens or closes the panel for the toggle that was activated.
   * Called via `on:click` on the submenu button.
   *
   * This is the entry point that makes the menu operable without a pointer. A screen
   * reader in browse mode intercepts focus and arrow keys, but forwards Enter and Space
   * on a button through as a click — so activation reaches us where focus never did.
   *
   * @param {MouseEvent} event
   */
  toggle(event) {
    const found = this.#resolveTrigger(event.target);
    if (!found) return;

    this.#clearTimers();

    if (found.panel === this.#activePanel) {
      this.#closeActivePanel();
      return;
    }

    this.#openPanel(found.panel, found.trigger);

    // The panels render in <sf-mega-menu>, after the whole <nav>, so an expanded panel's
    // content sits a dozen stops past the button that revealed it. aria-controls ties them
    // together but no screen reader navigates by it. Measured with NVDA: after expanding
    // EARRINGS, reading forward gave RINGS, NECKLACES, BRACELETS… never the panel.
    // Moving focus closes that gap without restructuring the markup. Only on activation —
    // hover must never steal focus.
    this.#focusFirstItem(found.panel);
  }

  /**
   * Schedules panel deactivation after a delay.
   * Called via `on:pointerleave="/deactivate"` on menu triggers and panels.
   *
   * @param {PointerEvent} _event
   */
  deactivate(_event) {
    this.#clearOpen();

    this.#closeDelay = window.setTimeout(() => {
      this.#closeDelay = null;
      this.#closeActivePanel();
    }, 300);
  }

  /**
   * Cancels a pending close when the pointer enters the panel.
   * Called via `on:pointerenter="/cancelClose"` on panels.
   */
  cancelClose() {
    this.#clearClose();
  }

  /**
   * Handles keydown on menu triggers. Escape closes the mega menu.
   *
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    // Escape is handled document-wide by #handleKeydown, so it works from inside a
    // panel too — not just while the trigger itself holds focus.

    // The trigger is a real link, so Enter still navigates to the category page.
    // ArrowDown is what opens the panel and steps into it, matching how a disclosure
    // menu is expected to behave.
    if (event.key === 'ArrowDown') {
      const found = this.#resolveTrigger(event.target);
      if (!found) return;

      event.preventDefault();
      this.#clearTimers();

      if (found.panel !== this.#activePanel) this.#openPanel(found.panel, found.trigger);
      this.#focusFirstItem(found.panel);
    }
  }

  /**
   * Immediately closes any open panel. Used for Escape key and external calls.
   */
  closeAll() {
    this.#clearTimers();
    this.#closeActivePanel();
  }

  /**
   * Opens a panel: sets ARIA states, measures height, and dispatches the open event.
   *
   * @param {HTMLElement} panel
   * @param {HTMLElement} trigger
   */
  #openPanel(panel, trigger) {
    if (this.#activePanel && this.#activePanel !== panel) {
      this.#closeActivePanel();
    }

    this.#activePanel = panel;
    this.#activeTrigger = trigger;

    trigger.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');
    this.dataset.open = 'true';

    requestAnimationFrame(() => {
      const height = panel.scrollHeight;
      this.style.setProperty('--sf-submenu-height', `${height}px`);
    });

    document.dispatchEvent(new CustomEvent('sf-mega-menu:open'));
  }

  /**
   * Closes the currently active panel and dispatches the close event.
   */
  #closeActivePanel() {
    if (!this.#activePanel) return;

    if (this.#activeTrigger) {
      this.#activeTrigger.setAttribute('aria-expanded', 'false');
    }

    this.#activePanel.setAttribute('aria-hidden', 'true');
    this.#activePanel = null;
    this.#activeTrigger = null;
    this.dataset.open = 'false';

    this.style.setProperty('--sf-submenu-height', '0px');

    document.dispatchEvent(new CustomEvent('sf-mega-menu:close'));
  }

  /**
   * Resolves the menu trigger an event came from, and the panel it controls.
   * Shared by the pointer, focus and keyboard entry points so they cannot drift apart.
   *
   * @param {EventTarget | null} target
   * @returns {{ trigger: HTMLElement, panel: HTMLElement } | null}
   */
  #resolveTrigger(target) {
    if (!(target instanceof HTMLElement)) return null;

    // Hover fires on the <li>, click and keydown on the button or the chevron inside it.
    // Either way the trigger we want is the button, because that is where aria-expanded
    // lives and what focus has to return to.
    const item = target.closest('.sf-header__menu-item');
    const trigger =
      target.closest('.sf-header__menu-toggle') ?? item?.querySelector('.sf-header__menu-toggle');
    if (!(trigger instanceof HTMLElement)) return null;

    // The button's own text is its visually-hidden label ("Show X submenu"), not the
    // category, so the panel is matched on data-menu-title instead of textContent.
    const panel = this.#findPanel((trigger.dataset.menuTitle ?? '').trim().toLowerCase());
    return panel ? { trigger, panel } : null;
  }

  /**
   * Moves focus to the first item of a panel that was just opened.
   * Deferred a frame: the panel is display: none until aria-hidden flips, and focus()
   * on a display: none element is a no-op.
   *
   * @param {HTMLElement} panel
   */
  #focusFirstItem(panel) {
    requestAnimationFrame(() => {
      const first = panel.querySelector('a[href], button:not([disabled])');
      if (first instanceof HTMLElement) first.focus();
    });
  }

  /**
   * Closes the open panel once focus lands outside every trigger and panel — the
   * keyboard counterpart of #handleClickOutside. Without it, tabbing past the menu
   * into the cart or search would leave a panel open behind the user.
   *
   * @param {FocusEvent} event
   */
  #handleFocusIn = (event) => {
    if (!this.#activePanel) return;
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    if (this.#resolveTrigger(event.target)) return;

    this.closeAll();
  };

  /**
   * Finds a panel whose `data-menu-title` matches the given title (case-insensitive).
   *
   * @param {string} title
   * @returns {HTMLElement | null}
   */
  #findPanel(title) {
    if (!this.refs.panel) return null;

    const panels = Array.isArray(this.refs.panel) ? this.refs.panel : [this.refs.panel];
    return panels.find((p) => (p.dataset.menuTitle ?? '').toLowerCase() === title) ?? null;
  }

  /**
   * Closes all panels when the Escape key is pressed.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#activePanel) return;

    const trigger = this.#activeTrigger;
    // Closing sets the panel to display: none. If focus is inside it, the browser drops
    // focus to <body> and the user loses their place in the page (WCAG 2.4.3), so it has
    // to go back to the trigger. Checked BEFORE closing, while the panel still holds it.
    const focusWasInPanel = this.contains(document.activeElement);

    this.closeAll();

    // Only reclaim focus when we took it away; otherwise this would yank the user out of
    // wherever they actually are.
    if (focusWasInPanel) {
      trigger?.focus();
    }
  };

  /**
   * Closes all panels when the user clicks outside the mega menu and its triggers.
   *
   * @param {MouseEvent} event
   */
  #handleClickOutside = (event) => {
    if (!this.#activePanel) return;
    if (!(event.target instanceof Node)) return;

    if (this.contains(event.target)) return;

    const header = this.closest('sf-header');
    if (header && header.contains(event.target)) return;

    this.closeAll();
  };

  /**
   * Cancels any pending open timeout.
   */
  #clearOpen() {
    if (this.#openDelay !== null) {
      clearTimeout(this.#openDelay);
      this.#openDelay = null;
    }
  }

  /**
   * Cancels any pending close timeout.
   */
  #clearClose() {
    if (this.#closeDelay !== null) {
      clearTimeout(this.#closeDelay);
      this.#closeDelay = null;
    }
  }

  /**
   * Cancels both open and close timeouts.
   */
  #clearTimers() {
    this.#clearOpen();
    this.#clearClose();
  }
}

if (!customElements.get('sf-mega-menu')) {
  customElements.define('sf-mega-menu', SfMegaMenu);
}
